/**
 * Mural Area Selector - Web Component
 * Allows users to enter wall dimensions and select a specific area of a mural image.
 * Selection coordinates are saved as Shopify line item properties.
 */
class MuralAreaSelector extends HTMLElement {
  constructor() {
    super();
    this.isDragging = false;
    this.dragStart = { x: 0, y: 0 };
    this.selectionStart = { x: 0, y: 0 };
    this.selection = { x: 0, y: 0, width: 0, height: 0 };
    this.unit = 'cm';
    this.imageLoaded = false;
    this.imageNaturalWidth = 0;
    this.imageNaturalHeight = 0;
    this.quoteTimer = null;
this.quoteRequestId = 0;
this.currentQuote = null;
this.quoteCache = new Map();
  }

  connectedCallback() {
    this.imageUrl = this.dataset.imageUrl;
    this.productId = this.dataset.productId;
    this.moneyFormat = this.dataset.moneyFormat || '£{{amount}}';
    this.country = String(this.dataset.country || '').trim().toUpperCase();

    this.bindElements();
    this.buildImagePicker();
    this.buildPricing();
    this.updateFromPrice();
    this.bindEvents();
    // Must run last: once the panel moves out of this element,
    // this.querySelector() no longer reaches it.
    this.relocateDimsPanel();
    const showEmptyPrice = () => {
  if ((window.JardeCustomTransport || window.JardeStagingTransport)?.isJardeProduct?.(this) && !this.getCurrentAreaM2()) this.updatePriceSummary();
};
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', showEmptyPrice, {once:true});
else showEmptyPrice();
this.widthInput.addEventListener('input', showEmptyPrice);
this.heightInput.addEventListener('input', showEmptyPrice);
document.addEventListener('variant:change', showEmptyPrice);
  }

  // Print size limits in cm. Keep in sync with the hint text in mural-area-selector.liquid.
  static MIN_W = 100;
  static MAX_W = 400;
  static MIN_H = 150;
  static MAX_H = 260;
  getPrintLimits() {
  const isMuance = (this.dataset.vendor || '').trim().toLowerCase() === 'muance';

  if (isMuance) {
  return {
    minW: 50,
    maxW: Infinity,
    minH: 50,
    maxH: 350
  };
}

  return {
    minW: MuralAreaSelector.MIN_W,
    maxW: MuralAreaSelector.MAX_W,
    minH: MuralAreaSelector.MIN_H,
    maxH: MuralAreaSelector.MAX_H
  };
}

  /**
   * Move the wall-size panel (inputs + limits hint + price summary) into the
   * product form, directly above the buy buttons, so shoppers set their wall
   * size before they can add to cart.
   */
  relocateDimsPanel() {
    this.dimsPanel = this.querySelector('[data-dims-panel]');
    if (!this.dimsPanel) return;
    const form = this.getProductForm();
    if (!form) return;
    const buttons = form.querySelector('.product-form__buttons');
    if (buttons && buttons.parentNode) {
      buttons.parentNode.insertBefore(this.dimsPanel, buttons);
      form.classList.add('mural-form--dims-injected');
    }
  }

  // Central JARDE made-to-measure pricing.
// Values come from Shop metafields via .jarde-pricing-config.
// Fallbacks keep the calculator working if a metafield is ever unavailable.
static get MATERIAL_RATES() {
  const fallback = {
    nonWoven: 39,
    linen: 44,
    concrete: 44,
    contract: 49
  };

  let pricing = fallback;

  try {
    const config = document.querySelector('.jarde-pricing-config');

    if (config) {
      const parsed = JSON.parse(config.textContent);

      pricing = {
        nonWoven: Number(parsed.nonWoven) || fallback.nonWoven,
        linen: Number(parsed.linen) || fallback.linen,
        concrete: Number(parsed.concrete) || fallback.concrete,
        contract: Number(parsed.contract) || fallback.contract
      };
    }
  } catch (error) {
    pricing = fallback;
  }

  return [
    {
      match: 'fabric-backed (contract)',
      rate: pricing.contract,
      label: 'Fabric-Backed (Contract)'
    },
    {
      match: 'fabric-backed contract',
      rate: pricing.contract,
      label: 'Fabric-Backed (Contract)'
    },
    {
      match: 'fabric backed contract',
      rate: pricing.contract,
      label: 'Fabric-Backed (Contract)'
    },
    {
      match: 'concrete texture',
      rate: pricing.concrete,
      label: 'Concrete Texture'
    },
    {
      match: 'linen texture',
      rate: pricing.linen,
      label: 'Linen Texture'
    },
    {
      match: 'non-woven',
      rate: pricing.nonWoven,
      label: 'Non-Woven'
    },
    {
      match: 'non woven',
      rate: pricing.nonWoven,
      label: 'Non-Woven'
    }
  ];
}

  buildPricing() {
    this.priceSummary = this.querySelector('[data-price-summary]');
    this.priceAreaEl = this.querySelector('[data-price-area]');
    this.priceMaterialEl = this.querySelector('[data-price-material]');
    this.priceTotalEl = this.querySelector('[data-price-total]');

    const dataEl = this.querySelector('.mural-selector__pricing-json');
    this.variantRates = {}; // variantId → { rate, label }
    if (dataEl) {
      let variants;
      try { variants = JSON.parse(dataEl.textContent); } catch (e) { variants = []; }
      variants.forEach(v => {
        const haystack = [v.title, v.option1, v.option2, v.option3]
          .filter(Boolean).join(' ').toLowerCase();
        const found = MuralAreaSelector.MATERIAL_RATES.find(m => haystack.includes(m.match));
        const price = typeof v.price === 'number' ? v.price : parseFloat(v.price);
        const carrierPrice = Number.isFinite(price) ? price : null;

        console.log('[JARDE PRICE CHECK]', {
  variant: v.title,
  carrierPrice: carrierPrice,
  currency: window.Shopify?.currency?.active,
  currencyRate: window.Shopify?.currency?.rate
});

        const currencyRate = parseFloat(
  window.Shopify?.currency?.rate || 1
);

const vendor = (this.dataset.vendor || '').toLowerCase();
const isJarde = vendor === 'jarde';

if (isJarde && found) {
  this.variantRates[v.variantId] = {
    rate: found.rate * currencyRate,
    carrierPrice: carrierPrice,
    label: found.label,
    unitBilling: false
  };
} else if (price > 0.02) {
  this.variantRates[v.variantId] = {
    rate: price,
    label: found ? found.label : (v.title || 'Material'),
    unitBilling: true
  };
} else if (found) {
  this.variantRates[v.variantId] = {
    rate: found.rate,
    label: found.label,
    unitBilling: false
  };
}
      });
    }

    // Watch the product form's variant input so we always know the current material.
    const form = this.getProductForm();
    if (form) {
      const idInput = form.querySelector('input[name="id"]');
      if (idInput) {
        new MutationObserver(() => this.updatePriceSummary())
          .observe(idInput, { attributes: true, attributeFilter: ['value'] });
      }
      form.addEventListener('change', () => setTimeout(() => this.updatePriceSummary(), 50));

      // Gate the theme's standard "Add to Cart" so it can't add a £0.01 line.
      // A valid wall size is REQUIRED — without one we block the submit, show a
      // message and focus the size inputs. Captures the click in the capture
      // phase so we run before the theme's own handler.
      
const guard = (e) => {
  const isMuance =
    String(this.dataset.vendor || '')
      .trim()
      .toLowerCase() === 'muance';

  const isMuanceStandard =
    isMuance &&
    document.querySelector(
      '.muance-configurator [data-muance-format="standard"].is-active'
    );

  if (isMuanceStandard) return;

  const mat = this.getCurrentRate();
  if (!mat) return;


        if (!this.hasValidSize()) {
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();
          this.setSizeWarning(
            'Please enter your wall size first — width 100–400 cm, height 150–260 cm — so we can calculate your price.'
          );
          const panel = this.dimsPanel || this;
          panel.scrollIntoView({ behavior: 'smooth', block: 'center' });
          setTimeout(() => this.widthInput.focus(), 400);
          return;
        }

        this.updateFormInputs();
        const area = this.getCurrentAreaM2();
        const qty = this.getQtyFor(area, mat);
        let qtyInput = form.querySelector('input[name="quantity"]');
        if (!qtyInput) {
          qtyInput = document.createElement('input');
          qtyInput.type = 'hidden';
          qtyInput.name = 'quantity';
          form.appendChild(qtyInput);
        }
        qtyInput.value = qty;
      };

      // Document-level capture: runs before the theme's ProductForm listeners
      // (capture on an ancestor always beats listeners on the target itself).
      document.addEventListener('submit', (e) => {
        if (e.target === form) guard(e);
      }, true);
      document.addEventListener('click', (e) => {
        const btn = e.target.closest && e.target.closest('button[name="add"], button.product-form__submit');
        if (!btn) return;
        if (btn.form === form || form.contains(btn)) guard(e);
      }, true);
    }
  }

  getCurrentVariantId() {
    const form = this.getProductForm();
    const input = form && form.querySelector('input[name="id"]');
    return input ? parseInt(input.value, 10) : null;
  }

  getCurrentRate() {
    const vid = this.getCurrentVariantId();
    return vid ? this.variantRates[vid] : null;
  }
  async updateFromPrice() {
  if (String(this.dataset.vendor || '').toLowerCase() === 'muance') return;
  const privateJarde=!!(window.JardeCustomTransport || window.JardeStagingTransport)?.isJardeProduct?.(this);
  const fromRequest=privateJarde ? (this.fromPriceRequestId=(this.fromPriceRequestId || 0)+1) : null;
  const priceElement =
    document.querySelector('[data-jarde-m2-price]');

  if (!priceElement || !this.variantRates) return;

  priceElement.textContent = privateJarde ? 'Updating price…' : 'From …/m²';

  const nonWovenEntry =
    Object.entries(this.variantRates).find(
      ([, rate]) => {
        const label = String(
          rate?.label || ''
        ).trim().toLowerCase();

        return (
          label === 'non-woven' ||
          label === 'non woven'
        );
      }
    );

  if (!nonWovenEntry) return;

  const [variantId, mat] = nonWovenEntry;

  const currency = String(
    window.Shopify?.currency?.active || 'GBP'
  ).toUpperCase();

  const country = String(
    this.country || ''
  ).toUpperCase();

  const cacheKey = [
    'from',
    variantId,
    currency,
    country
  ].join('|');

  let quote = this.quoteCache.get(cacheKey);

  try {
    if (!quote && (window.JardeCustomTransport || window.JardeStagingTransport)?.isJardeProduct?.(this)) {
      quote=await window.JardeMuanceBasket.quote({vendor:'Jarde',variantId:String(variantId),width:100,height:100,material:mat.label || 'Non-Woven'});
      this.quoteCache.set(cacheKey,quote);
    }
    if (!quote) {
      const response = await fetch(
        '/apps/jarde-pricing/quote',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          cache: 'no-store',
          body: JSON.stringify({
            variantId,
            width: 100,
            height: 100,
            material: mat.label || 'Non-Woven',
            currency,
            country
          })
        }
      );

      quote = await response.json();

      if (
        !response.ok ||
        !quote?.ok ||
        !quote?.market
      ) {
        throw new Error(
          quote?.error ||
          'Could not calculate From price.'
        );
      }

      this.quoteCache.set(cacheKey, quote);
    }

    if (privateJarde && fromRequest!==this.fromPriceRequestId) return;
    const marketRate =
      Number(quote.market.rate);

    if (!Number.isFinite(marketRate)) {
      throw new Error('Invalid market rate.');
    }

    const marketCurrency =
      quote.market.currency || currency;

    const formattedRate =
      new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: marketCurrency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(marketRate);

    priceElement.textContent =
      `From ${formattedRate}/m²`;
      if ((window.JardeCustomTransport || window.JardeStagingTransport)?.isJardeProduct?.(this) && !this.getCurrentAreaM2()) this.updatePriceSummary();

  } catch (error) {
    if (privateJarde && fromRequest!==this.fromPriceRequestId) return;
    console.warn(
      '[JARDE FROM PRICE]',
      error
    );

    priceElement.textContent =
      'From price unavailable';
  }
}
async getShopifyQuote() {
  if ((window.JardeCustomTransport || window.JardeStagingTransport)?.isJardeProduct?.(this)) {
    const ticket=++this.quoteRequestId;
    try {
      const factor=this.unit==='in'?2.54:1;
      const selection={vendor:'Jarde',variantId:String(this.getCurrentVariantId()),material:this.getCurrentRate()?.label,width:Number(this.widthInput?.value)*factor,height:Number(this.heightInput?.value)*factor};
      const quote=await window.JardeMuanceBasket.quote(selection);
      if (ticket!==this.quoteRequestId || selection.variantId!==String(this.getCurrentVariantId()) || selection.width!==Number(this.widthInput?.value)*factor || selection.height!==Number(this.heightInput?.value)*factor) return null;
      this.currentQuote=quote;return quote;
    } catch {if(ticket===this.quoteRequestId)this.currentQuote=null;return null;}
  }
  if (String(this.dataset.vendor || '').toLowerCase() === 'muance') {
    const wrapper=document.querySelector('.muance-configurator');
    const selection=wrapper && window.JardeMuanceSelection?.getSelection(wrapper);
    const variant=selection?.format === 'custom' && window.JardeMuanceSelection.findVariant(selection);
    if (!variant || !window.JardeMuanceBasket) return null;
    const ticket=++this.quoteRequestId;
    try {
      const result=await window.JardeMuanceBasket.quote({variantId:String(variant.id),width:selection.width,height:selection.height,material:selection.materialLabel});
      if (ticket !== this.quoteRequestId || JSON.stringify(window.JardeMuanceSelection.getSelection(wrapper)) !== JSON.stringify(selection)) return null;
      this.currentQuote=result;
      return result;
    } catch {this.currentQuote=null;return null;}
  }
  const variantId = this.getCurrentVariantId();
  const mat = this.getCurrentRate();

  const widthValue = parseFloat(this.widthInput?.value);
  const heightValue = parseFloat(this.heightInput?.value);

  if (
    !variantId ||
    !mat ||
    !Number.isFinite(widthValue) ||
    !Number.isFinite(heightValue) ||
    widthValue <= 0 ||
    heightValue <= 0
  ) {
    return null;
  }

  const widthCm =
    this.unit === 'in'
      ? widthValue * 2.54
      : widthValue;

  const heightCm =
    this.unit === 'in'
      ? heightValue * 2.54
      : heightValue;

  const currency = String(
    window.Shopify?.currency?.active || 'GBP'
  ).toUpperCase();

  const country = String(
    this.country || ''
  ).toUpperCase();

  
const material =
  String(this.dataset.vendor || '').toLowerCase() === 'muance'
    ? (
        document.querySelector(
          '.muance-configurator [data-muance-material].is-active'
        )?.dataset.muanceLabel || ''
      )
    : (mat.label || '');

  const cacheKey = [
    variantId,
    widthCm.toFixed(2),
    heightCm.toFixed(2),
    material,
    currency,
    country
  ].join('|');

  if (this.quoteCache.has(cacheKey)) {
    const cachedQuote = this.quoteCache.get(cacheKey);
    this.currentQuote = cachedQuote;
    return cachedQuote;
  }

  const requestId = ++this.quoteRequestId;

  try {
    const response = await fetch(
      '/apps/jarde-pricing/quote',
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        cache: 'no-store',
        body: JSON.stringify({
          variantId,
          width: Number(widthCm.toFixed(2)),
          height: Number(heightCm.toFixed(2)),
          material,
          currency,
          country
        })
      }
    );

    const data = await response.json();

    if (
      !response.ok ||
      !data?.ok ||
      !data?.market
    ) {
      throw new Error(
        data?.error || 'Could not calculate Shopify market price.'
      );
    }

    if (requestId !== this.quoteRequestId) {
      return null;
    }

    this.quoteCache.set(cacheKey, data);
    this.currentQuote = data;

    return data;
  } catch (error) {
    console.warn(
      '[JARDE QUOTE]',
      error
    );

    return null;
  }
}
  // Cart quantity for a given area/material. £0.01 variants encode pence in the
  // quantity; real-priced variants are billed per whole m².
  getQtyFor(area, mat) {
  if (String(this.dataset.vendor || '').toLowerCase() === 'muance' || ((window.JardeCustomTransport || window.JardeStagingTransport)?.isJardeProduct?.(this))) return 1;
  if (mat.unitBilling) {
    return Math.max(1, Math.ceil(area - 1e-9));
  }

  const carrierPrice = Number(mat.carrierPrice);
  const targetTotal = area * mat.rate;

  if (!Number.isFinite(carrierPrice) || carrierPrice <= 0) {
    return 1;
  }

  return Math.max(1, Math.round(targetTotal / carrierPrice));
}


  // The amount the customer will actually pay for a given area/material.
  getTotalFor(area, mat) {
    if (mat.unitBilling) return this.getQtyFor(area, mat) * mat.rate;
    return area * mat.rate;
  }

  getCurrentAreaM2() {
    const w = parseFloat(this.widthInput.value);
    const h = parseFloat(this.heightInput.value);
    if (!w || !h || w <= 0 || h <= 0) return 0;
    const widthCm = this.unit === 'in' ? w * 2.54 : w;
    const heightCm = this.unit === 'in' ? h * 2.54 : h;
    return (widthCm * heightCm) / 10000;
  }

  // True only when both dimensions are entered AND within the printable range.
  hasValidSize() {
  const w = parseFloat(this.widthInput.value);
  const h = parseFloat(this.heightInput.value);

  if (!w || !h || w <= 0 || h <= 0) return false;

  const widthCm = this.unit === 'in' ? w * 2.54 : w;
  const heightCm = this.unit === 'in' ? h * 2.54 : h;

  const limits = this.getPrintLimits();

  return (
    widthCm >= limits.minW &&
    widthCm <= limits.maxW &&
    heightCm >= limits.minH &&
    heightCm <= limits.maxH
  );
}

  formatMoney(amount) {
    // Minimal Shopify money_format renderer — supports {{amount}} and {{amount_no_decimals}}.
    const fmt = this.moneyFormat;
    const fixed = amount.toFixed(2);
    const noDec = Math.round(amount).toString();
    return fmt
      .replace(/\{\{\s*amount\s*\}\}/g, fixed)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, noDec)
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, fixed.replace('.', ','))
      .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, noDec);
  }

  updatePriceSummary() {
  if (!this.priceSummary) return;
  const privateJarde=!!(window.JardeCustomTransport || window.JardeStagingTransport)?.isJardeProduct?.(this);
  const summaryRequest=privateJarde ? (this.priceSummaryRequestId=(this.priceSummaryRequestId || 0)+1) : null;

  const area = this.getCurrentAreaM2();
  const mat = this.getCurrentRate();

  clearTimeout(this.quoteTimer);

  if (privateJarde) {
  if (!this.emptyPriceEl) {
    this.emptyPriceEl = document.createElement('p');
    this.priceSummary.before(this.emptyPriceEl);
  }
  this.emptyPriceEl.hidden = !!area || !mat;
}

if (!area || !mat) {
  this.currentQuote = null;
  this.priceSummary.hidden = true;

  if (privateJarde && mat) {
    this.emptyPriceEl.textContent = 'Enter your wall size · Updating price…';

    const variantId = String(this.getCurrentVariantId());
    const currency = String(window.Shopify?.currency?.active || 'GBP').toUpperCase();
    const country = String(this.country || '').toUpperCase();
    const cacheKey = ['from', variantId, currency, country].join('|');

    this.quoteTimer = setTimeout(async () => {
      try {
        let quote = this.quoteCache.get(cacheKey);
        if (!quote) {
          quote = await window.JardeMuanceBasket.quote({
            vendor: 'Jarde',
            variantId,
            width: 100,
            height: 100,
            material: mat.label
          });
          this.quoteCache.set(cacheKey, quote);
        }

        if (
          summaryRequest !== this.priceSummaryRequestId ||
          variantId !== String(this.getCurrentVariantId()) ||
          this.getCurrentAreaM2()
        ) return;

        if (
          currency !== String(window.Shopify?.currency?.active || 'GBP').toUpperCase() ||
          country !== String(this.country || '').toUpperCase()
        ) return;

        const rate = Number(quote.market?.rate);

        if (
          quote.market?.currency !== currency ||
          !Number.isFinite(rate) ||
          rate <= 0
        ) throw new Error('Invalid market rate.');

        const formatted = new Intl.NumberFormat(
          document.documentElement.lang || 'en-GB',
          {
            style: 'currency',
            currency,
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
          }
        ).format(rate);

        this.emptyPriceEl.textContent =
          `Enter your wall size · ${formatted}/m²`;

      } catch {
        if (summaryRequest === this.priceSummaryRequestId) {
          this.emptyPriceEl.textContent =
            'Enter your wall size · Price unavailable';
        }
      }
    }, 150);
  }

  return;
}

  this.priceSummary.hidden = false;

  if (mat.unitBilling && String(this.dataset.vendor || '').toLowerCase() !== 'muance' && !((window.JardeCustomTransport || window.JardeStagingTransport)?.isJardeProduct?.(this))) {
    const qty = this.getQtyFor(area, mat);

    this.priceAreaEl.textContent =
      `${area.toFixed(2)} m² (billed as ${qty} m²)`;
  } else {
    this.priceAreaEl.textContent =
      `${area.toFixed(2)} m²`;
  }

  this.priceMaterialEl.textContent =
    `${mat.label} · ${privateJarde ? 'Updating price…' : 'Calculating…'}`;

  this.priceTotalEl.textContent =
    privateJarde ? 'Updating price…' : 'Calculating…';

  this.quoteTimer = setTimeout(async () => {
    const quote = await this.getShopifyQuote();
    if (privateJarde && summaryRequest!==this.priceSummaryRequestId) return;

    if (!quote?.market) {
      this.priceMaterialEl.textContent =
        `${mat.label} · Price unavailable`;

      this.priceTotalEl.textContent =
        'Price unavailable';

      return;
    }

    const marketRate = Number(quote.market.rate);
    const marketTotal = Number(quote.market.total);

    if (
      !Number.isFinite(marketRate) ||
      !Number.isFinite(marketTotal) ||
      (privateJarde && (marketRate<=0 || marketTotal<=0))
    ) {
      if (privateJarde) {
        this.currentQuote=null;
        this.priceMaterialEl.textContent=`${mat.label} · Price unavailable`;
        this.priceTotalEl.textContent='Price unavailable';
      }
      return;
    }

    if (privateJarde && /^non[ -]woven$/i.test(String(mat.label).trim())) {
      const from=document.querySelector('[data-jarde-m2-price]');
      if (from) {
        this.fromPriceRequestId=(this.fromPriceRequestId || 0)+1;
        from.textContent=`From ${this.formatMoney(marketRate)}/m²`;
      }
    }

    this.priceMaterialEl.textContent =
      `${mat.label} · ${this.formatMoney(marketRate)}/m²`;

    this.priceTotalEl.textContent =
      privateJarde ? `${this.formatMoney(marketTotal)} · ${this.formatMoney(marketRate)}/m²` : this.formatMoney(marketTotal);

    document.dispatchEvent(
      new CustomEvent('jarde:market-quote', {
        detail: quote
      })
    );
  }, 150);
}

  bindElements() {
    this.widthInput = this.querySelector('[data-width]');
    this.heightInput = this.querySelector('[data-height]');
    this.unitBtns = this.querySelectorAll('.mural-selector__unit-btn');
    this.workspace = this.querySelector('.mural-selector__workspace');
    this.imageContainer = this.querySelector('.mural-selector__image-container');
    this.image = this.querySelector('.mural-selector__image');
    this.selectionBox = this.querySelector('.mural-selector__selection');
    this.selectionInfo = this.querySelector('.mural-selector__selection-info');
    this.previewCanvas = this.querySelector('.mural-selector__preview-canvas');
    this.previewDimensions = this.querySelector('.mural-selector__preview-dimensions');
    this.instructions = this.querySelector('.mural-selector__instructions');
    this.imagePicker = this.querySelector('.mural-selector__image-picker');
    this.sizeWarning = this.querySelector('[data-size-warning]');
    this.addBar = this.querySelector('[data-add-bar]');
    this.addToCartBtn = this.querySelector('[data-add-to-cart]');
  }

  buildImagePicker() {
    const dataEl = this.querySelector('.mural-selector__media-json');
    if (!dataEl) return;
    let images;
    try { images = JSON.parse(dataEl.textContent); } catch (e) { return; }

    this.variantImages = images;

    // Only show picker strip if there are multiple images
    if (images.length > 1) {
      const label = document.createElement('p');
      label.className = 'mural-selector__picker-label';
      label.textContent = 'Select the mural pattern image:';

      const strip = document.createElement('div');
      strip.className = 'mural-selector__picker-strip';

      const activeUrl = this.imageUrl.split('?')[0];

      images.forEach((img, i) => {
        const isActive = img.url.split('?')[0] === activeUrl;
        const thumb = document.createElement('button');
        thumb.type = 'button';
        thumb.className = 'mural-selector__picker-thumb' + (isActive ? ' active' : '');
        thumb.dataset.variantId = img.variantId || '';
        thumb.innerHTML = `<img src="${img.thumb}" alt="Image ${i + 1}" loading="lazy">`;
        thumb.addEventListener('click', () => {
          this.imagePicker.querySelectorAll('.mural-selector__picker-thumb').forEach(t => t.classList.remove('active'));
          thumb.classList.add('active');
          this.switchImage(img.url);
        });
        strip.appendChild(thumb);
      });

      this.imagePicker.appendChild(label);
      this.imagePicker.appendChild(strip);
    }

    // Listen for variant changes on the product form
    this.listenForVariantChanges();
  }

  listenForVariantChanges() {
    if (!this.variantImages || this.variantImages.length < 2) return;

    const form = this.getProductForm();
    if (!form) return;

    const observer = new MutationObserver(() => {
      const input = form.querySelector('input[name="id"]');
      if (!input) return;
      const vid = parseInt(input.value);
      const match = this.variantImages.find(v => v.variantId === vid);
      if (match && match.url !== this.imageUrl) {
        this.switchImage(match.url);
        // Update active thumbnail
        this.imagePicker.querySelectorAll('.mural-selector__picker-thumb').forEach(t => {
          t.classList.toggle('active', t.dataset.variantId == vid);
        });
      }
    });

    const idInput = form.querySelector('input[name="id"]');
    if (idInput) {
      observer.observe(idInput, { attributes: true, attributeFilter: ['value'] });
      // Also listen for direct value changes via event
      form.addEventListener('change', () => {
        setTimeout(() => {
          const vid = parseInt(form.querySelector('input[name="id"]').value);
          const match = this.variantImages.find(v => v.variantId === vid);
          if (match && match.url !== this.imageUrl) {
            this.switchImage(match.url);
            this.imagePicker.querySelectorAll('.mural-selector__picker-thumb').forEach(t => {
              t.classList.toggle('active', t.dataset.variantId == vid);
            });
          }
        }, 100);
      });
    }
  }

  switchImage(url) {
    this.imageUrl = url;
    this.imageLoaded = false;
    this.workspace.classList.remove('visible');
    this.selectionBox.style.display = 'none';
    this.loadImage();
    // Re-trigger dimensions if already filled
    if (this.widthInput.value && this.heightInput.value) {
      this.image.addEventListener('load', () => this.onDimensionChange(), { once: true });
    }
  }

  bindEvents() {
    this.widthInput.addEventListener('input', () => this.onDimensionChange());
    this.heightInput.addEventListener('input', () => this.onDimensionChange());

    this.unitBtns.forEach(btn => {
      btn.addEventListener('click', () => this.toggleUnit(btn.dataset.unit));
    });

    // Drag events on selection box
    this.selectionBox.addEventListener('pointerdown', (e) => this.onDragStart(e));
    document.addEventListener('pointermove', (e) => this.onDragMove(e));
    document.addEventListener('pointerup', () => this.onDragEnd());

    // Add to cart
    if (this.addToCartBtn) {
      this.addToCartBtn.addEventListener('click', () => this.addToCart());
    }

    // Load the mural image
    this.loadImage();
  }

  loadImage() {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      this.imageNaturalWidth = img.naturalWidth;
      this.imageNaturalHeight = img.naturalHeight;
      this.image.src = img.src;
      this.imageLoaded = true;
    };
    img.src = this.imageUrl;
  }

  toggleUnit(unit) {
    if (unit === this.unit) return;

    const prevUnit = this.unit;
    this.unit = unit;

    this.unitBtns.forEach(btn => {
      btn.classList.toggle('active', btn.dataset.unit === unit);
    });

    // Convert existing values
    const widthVal = parseFloat(this.widthInput.value);
    const heightVal = parseFloat(this.heightInput.value);

    if (!isNaN(widthVal)) {
      this.widthInput.value = this.convertUnit(widthVal, prevUnit, unit).toFixed(1);
    }
    if (!isNaN(heightVal)) {
      this.heightInput.value = this.convertUnit(heightVal, prevUnit, unit).toFixed(1);
    }

    this.updatePreviewDimensions();
  }

  convertUnit(value, from, to) {
    if (from === 'cm' && to === 'in') return value / 2.54;
    if (from === 'in' && to === 'cm') return value * 2.54;
    return value;
  }

  onDimensionChange() {
    const width = parseFloat(this.widthInput.value);
    const height = parseFloat(this.heightInput.value);

    if (!width || !height || width <= 0 || height <= 0 || !this.imageLoaded) {
      this.workspace.classList.remove('visible');
      if (this.addBar) this.addBar.classList.remove('visible');
      this.setSizeWarning('');
      return;
    }

    // Validate against print size limits (always compared in cm)
    const widthCm = this.unit === 'in' ? width * 2.54 : width;
const heightCm = this.unit === 'in' ? height * 2.54 : height;

const limits = this.getPrintLimits();
const issues = [];

if (widthCm < limits.minW || widthCm > limits.maxW) {
  issues.push('width');
}

if (heightCm < limits.minH || heightCm > limits.maxH) {
  issues.push('height');
}

if (issues.length) {
  let warningMessage = '';

  const widthInvalid = issues.includes('width');
  const heightInvalid = issues.includes('height');

  if (widthInvalid && heightInvalid) {
    if (Number.isFinite(limits.maxW)) {
      warningMessage =
        `Width must be between ${limits.minW} and ${limits.maxW} cm, and height must be between ${limits.minH} and ${limits.maxH} cm.`;
    } else {
      warningMessage =
        `Width must be at least ${limits.minW} cm, and height must be between ${limits.minH} and ${limits.maxH} cm.`;
    }
  } else if (widthInvalid) {
    if (Number.isFinite(limits.maxW)) {
      warningMessage =
        `Width must be between ${limits.minW} and ${limits.maxW} cm.`;
    } else {
      warningMessage =
        `Width must be at least ${limits.minW} cm.`;
    }
  } else if (heightInvalid) {
    warningMessage =
      `Height must be between ${limits.minH} and ${limits.maxH} cm.`;
  }

  this.setSizeWarning(warningMessage);
  this.workspace.classList.remove('visible');
  if (this.addBar) this.addBar.classList.remove('visible');
  return;
}

this.setSizeWarning('');

    this.workspace.classList.add('visible');
    if (this.addBar) this.addBar.classList.add('visible');

    if (String(this.dataset.vendor || '').toLowerCase() === 'muance') {
      const rect=this.image.getBoundingClientRect();
      if (!rect.width || !rect.height) return;
      const key=JSON.stringify([widthCm,heightCm,this.imageUrl]);
      if (this.muanceCropKey !== key || !this.muanceCrop) {
        const scale=Math.max(1,widthCm/500,heightCm/270);
        const w=widthCm/(500*scale),h=heightCm/(270*scale);
        const cx=this.muanceCrop ? this.muanceCrop.x+this.muanceCrop.width/2 : 0.5;
        const cy=this.muanceCrop ? this.muanceCrop.y+this.muanceCrop.height/2 : 0.5;
        this.muanceCrop={x:Math.max(0,Math.min(1-w,cx-w/2)),y:Math.max(0,Math.min(1-h,cy-h/2)),width:w,height:h};
        this.muanceCropKey=key;
      }
      const crop=this.muanceCrop;
      this.selection={x:crop.x*rect.width,y:crop.y*rect.height,width:crop.width*rect.width,height:crop.height*rect.height};
      this.updateSelectionBox();this.updatePreview();this.updateFormInputs();this.updatePriceSummary();
      return;
    }

    // Calculate selection box size based on wall aspect ratio
    const wallAspect = width / height;
    const containerRect = this.imageContainer.getBoundingClientRect();
    const imgRect = this.image.getBoundingClientRect();
    const containerW = imgRect.width;
    const containerH = imgRect.height;

    if (containerW === 0 || containerH === 0) {
      // Image not rendered yet, retry
      requestAnimationFrame(() => this.onDimensionChange());
      return;
    }

    const imageAspect = containerW / containerH;

    let selW, selH;
    if (wallAspect > imageAspect) {
      // Wall is wider than image - fit to width
      selW = containerW * 0.9;
      selH = selW / wallAspect;
    } else {
      // Wall is taller - fit to height
      selH = containerH * 0.9;
      selW = selH * wallAspect;
    }

    // Ensure selection doesn't exceed image bounds
    selW = Math.min(selW, containerW);
    selH = Math.min(selH, containerH);

    // Keep the current crop position where possible.
// Only centre it the first time the selector opens.
let selX;
let selY;

if (this.selection.width > 0 && this.selection.height > 0) {
  const oldCenterX = this.selection.x + (this.selection.width / 2);
  const oldCenterY = this.selection.y + (this.selection.height / 2);

  const centerXPct = oldCenterX / containerW;
  const centerYPct = oldCenterY / containerH;

  selX = (centerXPct * containerW) - (selW / 2);
  selY = (centerYPct * containerH) - (selH / 2);

  selX = Math.max(0, Math.min(selX, containerW - selW));
  selY = Math.max(0, Math.min(selY, containerH - selH));
} else {
  selX = (containerW - selW) / 2;
  selY = (containerH - selH) / 2;
}

this.selection = { x: selX, y: selY, width: selW, height: selH };
    this.updateSelectionBox();
    this.updatePreview();
    this.updateFormInputs();
    this.updatePriceSummary();
  }

  setSizeWarning(message) {
    if (!this.sizeWarning) return;
    if (message) {
      this.sizeWarning.textContent = message;
      this.sizeWarning.hidden = false;
    } else {
      this.sizeWarning.textContent = '';
      this.sizeWarning.hidden = true;
    }
  }

  onDragStart(e) {
    e.preventDefault();
    this.isDragging = true;
    this.selectionBox.setPointerCapture(e.pointerId);

    const imgRect = this.image.getBoundingClientRect();
    this.dragStart = {
      x: e.clientX - imgRect.left - this.selection.x,
      y: e.clientY - imgRect.top - this.selection.y
    };
  }

  onDragMove(e) {
    if (!this.isDragging) return;
    e.preventDefault();

    const imgRect = this.image.getBoundingClientRect();
    const containerW = imgRect.width;
    const containerH = imgRect.height;

    let newX = e.clientX - imgRect.left - this.dragStart.x;
    let newY = e.clientY - imgRect.top - this.dragStart.y;

    // Constrain within image bounds
    newX = Math.max(0, Math.min(newX, containerW - this.selection.width));
    newY = Math.max(0, Math.min(newY, containerH - this.selection.height));

    this.selection.x = newX;
    this.selection.y = newY;

    if (String(this.dataset.vendor || '').toLowerCase() === 'muance') {
      this.muanceCrop={x:this.selection.x/containerW,y:this.selection.y/containerH,width:this.selection.width/containerW,height:this.selection.height/containerH};
    }
    this.updateSelectionBox();
    this.updatePreview();
    this.updateFormInputs();
  }

  onDragEnd() {
    this.isDragging = false;
  }

  updateSelectionBox() {
    const s = this.selection;
    this.selectionBox.style.left = s.x + 'px';
    this.selectionBox.style.top = s.y + 'px';
    this.selectionBox.style.width = s.width + 'px';
    this.selectionBox.style.height = s.height + 'px';
    this.selectionBox.style.display = 'block';

    // Update info badge
    const widthVal = this.widthInput.value;
    const heightVal = this.heightInput.value;
    const unitLabel = this.unit === 'cm' ? 'cm' : 'in';
    this.selectionInfo.textContent = `${widthVal} × ${heightVal} ${unitLabel}`;

    this.updatePreviewDimensions();
  }

  updatePreviewDimensions() {
    const widthVal = this.widthInput.value;
    const heightVal = this.heightInput.value;
    const unitLabel = this.unit === 'cm' ? 'cm' : 'in';
    this.previewDimensions.textContent = `Wall size: ${widthVal} × ${heightVal} ${unitLabel}`;
  }

  updatePreview() {
    if (!this.imageLoaded) return;

    const canvas = this.previewCanvas;
    const ctx = canvas.getContext('2d');
    const imgRect = this.image.getBoundingClientRect();

    // Map selection to natural image coordinates
    const scaleX = this.imageNaturalWidth / imgRect.width;
    const scaleY = this.imageNaturalHeight / imgRect.height;

    const sx = this.selection.x * scaleX;
    const sy = this.selection.y * scaleY;
    const sw = this.selection.width * scaleX;
    const sh = this.selection.height * scaleY;

    // Set canvas to match wall aspect ratio
    const wallAspect = parseFloat(this.widthInput.value) / parseFloat(this.heightInput.value);
    canvas.width = 600;
    canvas.height = Math.round(600 / wallAspect);

    ctx.drawImage(this.image, sx, sy, sw, sh, 0, 0, canvas.width, canvas.height);
  }

  getProductForm() {
    // The id must START with "ProductForm-" — a plain *= match would also hit the
    // payment-installments form (ProductFormInstallment-…), which comes first in
    // the DOM and has no buy buttons.
    return document.querySelector(`form[id^="ProductForm-"][id*="${this.productId}"]`)
      || document.querySelector(`form[data-type="add-to-cart-form"][id*="${this.productId}"]`);
  }

  updateFormInputs() {
    const form = this.getProductForm();
    if (!form) return;

    const imgRect = this.image.getBoundingClientRect();
    const containerW = imgRect.width;
    const containerH = imgRect.height;

    // Calculate percentages
    const xPct = ((this.selection.x / containerW) * 100).toFixed(String(this.dataset.vendor || '').toLowerCase()==='muance'?6:1);
    const yPct = ((this.selection.y / containerH) * 100).toFixed(String(this.dataset.vendor || '').toLowerCase()==='muance'?6:1);
    const wPct = ((this.selection.width / containerW) * 100).toFixed(String(this.dataset.vendor || '').toLowerCase()==='muance'?6:1);
    const hPct = ((this.selection.height / containerH) * 100).toFixed(String(this.dataset.vendor || '').toLowerCase()==='muance'?6:1);

    // Wall dimensions in cm (always store in cm)
    let wallW = parseFloat(this.widthInput.value);
    let wallH = parseFloat(this.heightInput.value);
    if (this.unit === 'in') {
      wallW = wallW * 2.54;
      wallH = wallH * 2.54;
    }

    // Customer-facing properties (shown in cart, checkout and order emails).
    this.setFormInput(form, 'properties[Wall Width (cm)]', wallW.toFixed(1));
    this.setFormInput(form, 'properties[Wall Height (cm)]', wallH.toFixed(1));

    // Technical properties — the "_" prefix hides them from the cart and
    // checkout, but they remain visible to the merchant on the admin order.
    this.setFormInput(form, 'properties[_Selection X%]', xPct);
    this.setFormInput(form, 'properties[_Selection Y%]', yPct);
    this.setFormInput(form, 'properties[_Selection Width%]', wPct);
    this.setFormInput(form, 'properties[_Selection Height%]', hPct);
    this.setFormInput(form, 'properties[_Mural Section]',
      `From ${xPct}% left, ${yPct}% top — ${wPct}% wide × ${hPct}% tall of the full mural`);
const productHandle = this.dataset.productHandle || '';

const cropPreviewUrl =
  window.location.origin +
  '/pages/mural-crop-preview' +
  '?handle=' + encodeURIComponent(productHandle) +
  '&x=' + encodeURIComponent(xPct) +
  '&y=' + encodeURIComponent(yPct) +
  '&w=' + encodeURIComponent(wPct) +
  '&h=' + encodeURIComponent(hPct) +
  '&wallW=' + encodeURIComponent(wallW.toFixed(1)) +
  '&wallH=' + encodeURIComponent(wallH.toFixed(1));

this.setFormInput(
  form,
  'properties[_Crop Preview]',
  cropPreviewUrl
);
    if (String(this.dataset.vendor || '').toLowerCase() === 'muance') return;

    // Pricing line item properties
    const area = this.getCurrentAreaM2();
    const mat = this.getCurrentRate();
    if (area && mat) {
      let areaText = `${area.toFixed(2)} m²`;
      if (mat.unitBilling) areaText += ` (billed as ${this.getQtyFor(area, mat)} m²)`;
      this.setFormInput(form, 'properties[Area]', areaText);
      this.setFormInput(form, 'properties[Material]', mat.label);
      const quotedRate = Number(
  this.currentQuote?.market?.rate
);

const quotedTotal = Number(
  this.currentQuote?.market?.total
);

const rateForProperties =
  Number.isFinite(quotedRate)
    ? quotedRate
    : mat.rate;

const totalForProperties =
  Number.isFinite(quotedTotal)
    ? quotedTotal
    : this.getTotalFor(area, mat);

this.setFormInput(
  form,
  'properties[Rate]',
  `${this.formatMoney(rateForProperties)}/m²`
);

this.setFormInput(
  form,
  'properties[_Calculated Total]',
  this.formatMoney(totalForProperties)
);
    }
  }

  addToCart() {
    const form = this.getProductForm();
    if (!form) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    // A valid wall size is required — no 1 m² fallback.
    if (!this.hasValidSize()) {
      this.setSizeWarning(
        'Please enter your wall size first — width 100–400 cm, height 150–260 cm — so we can calculate your price.'
      );
      (this.dimsPanel || this).scrollIntoView({ behavior: 'smooth', block: 'center' });
      setTimeout(() => this.widthInput.focus(), 400);
      return;
    }

    if (String(this.dataset.vendor || '').toLowerCase() === 'muance') {
      if (document.querySelector('.muance-configurator [data-muance-format="custom"].is-active')) {
        if (window.JardeMuanceBasket?.add) window.JardeMuanceBasket.add();
        else window.alert('Custom mural checkout is not ready.');
        return;
      }
      form.requestSubmit();
      return;
    }
    if ((window.JardeCustomTransport || window.JardeStagingTransport)?.isJardeProduct?.(this)) {
      if (window.JardeMuanceBasket?.add) window.JardeMuanceBasket.add();
      else window.alert('Custom mural checkout is not ready.');
      return;
    }
    this.updateFormInputs();

    // Override quantity = round(area × £/m² × 100) so a variant priced at £0.01
    // multiplies out to the real area-based total.
    const mat = this.getCurrentRate();
    if (mat) {
      const area = this.getCurrentAreaM2();
      const qty = this.getQtyFor(area, mat);
      let qtyInput = form.querySelector('input[name="quantity"]');
      if (!qtyInput) {
        qtyInput = document.createElement('input');
        qtyInput.type = 'hidden';
        qtyInput.name = 'quantity';
        form.appendChild(qtyInput);
      }
      qtyInput.value = qty;
    }

    // Trigger the theme's add-to-cart button so cart drawer / notifications fire correctly
    const submitBtn = form.querySelector('button[name="add"], button[type="submit"][name="add"]')
      || form.querySelector('button[type="submit"]');
    if (submitBtn) {
      submitBtn.click();
    } else {
      form.requestSubmit ? form.requestSubmit() : form.submit();
    }
  }

  setFormInput(form, name, value) {
    let input = form.querySelector(`input[name="${name}"]`);
    if (!input) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      form.appendChild(input);
    }
    input.value = value;
  }
}

customElements.define('mural-area-selector', MuralAreaSelector);

/**
 * Badge product gallery images: variant images get "Mural", all others get "Example".
 * Uses the variant media JSON to know which images are real mural patterns.
 */
function badgeProductImages() {
  const dataEl = document.querySelector('.mural-selector__media-json');
  if (!dataEl) return;

  let variantImages;
  try { variantImages = JSON.parse(dataEl.textContent); } catch (e) { return; }

  // Build a set of variant image URL stems (without query params) for fast lookup
  const muralUrls = new Set(variantImages.map(v => v.url.split('?')[0].split('/').pop()));

  document.querySelectorAll('.product__media').forEach(item => {
    if (item.querySelector('.product-img-badge')) return;

    const img = item.querySelector('img');
    if (!img) return;

    const src = img.getAttribute('src') || img.getAttribute('srcset') || '';
    const filename = src.split('/').pop().split('?')[0];

    // Check if any variant image filename is a substring (handles width suffixes like _400x)
    const isMural = [...muralUrls].some(muralFile => {
      const base = muralFile.replace(/\.[^.]+$/, ''); // strip extension
      return filename.includes(base);
    });

    const badge = document.createElement('span');
    badge.className = 'product-img-badge product-img-badge--' + (isMural ? 'mural' : 'example');
    badge.textContent = isMural ? 'Mural' : 'Example';
    item.appendChild(badge);
  });
}

document.addEventListener('DOMContentLoaded', badgeProductImages);
